import * as helpers from "./helpers";

// ========================= core =========================
/**
 * Calcula totais do pedido a partir dos movimentoItems.
 * @param {Array} movimentoItems
 * @param {Object} options
 * @param {boolean} [options.considerarFator=true] - Multiplica base por item.fator se existir.
 * @param {boolean} [options.retornaItensCalculados=false] - Retorna array com totais derivados por item.
 * @returns {{
 *   valores: {
 *     bruto: number,                 // soma das bases (qtd*unit*[fator])
 *     descontoItens: number,         // soma dos descontos por item (valor ou %)
 *     subtotalSemFinanceiro: number, // bruto - descontoItens
 *     descontoFinanceiro: number,    // soma dos descontos financeiros por item (descontoFinanceiroItem ou descontoFinanceiro)
 *     subtotalComFinanceiro: number, // subtotalSemFinanceiro - descontoFinanceiro
 *     acrescimos: number,            // soma de frete/acréscimos por item (se existirem)
 *     total: number,                 // subtotalComFinanceiro + acrescimos
 *   },
 *   pesos: {
 *     bruto: number,   // kg (ou unidade usada)
 *     liquido: number, // kg (ou unidade usada)
 *   },
 *   quantidades: {
 *     itens: number,         // quantidade de linhas
 *     unidades: number,      // soma de quantidades
 *   },
 *   precoUnitario: {
 *     maior: number,
 *     menor: number,
 *     mediaPonderada: number // (sum(qtd*unit)/sum(qtd))
 *   },
 *   itensCalculados?: Array<{
 *     ...itemOriginal,
 *     _base: number,
 *     _descontoItem: number,
 *     _totalSemFinanceiro: number,
 *     _descontoFinanceiroItem: number,
 *     _totalComFinanceiro: number,
 *     _acrescimosItem: number
 *   }>
 * }}
 */
export const obterValoresByMovimentoItens = (movimentoItems, { considerarFator = true, retornaItensCalculados = false } = {}) => {
    let brutoC = 0; // em centavos
    let descontoItensC = 0;
    let subtotalSemFinC = 0;
    let descontoFinC = 0;
    let subtotalComFinC = 0;
    let acrescimosC = 0;

    let pesoBruto = 0;
    let pesoLiquido = 0;

    let somaQtd = 0;
    let somaQtdParaMedia = 0; // para média ponderada do unitário
    let somaQtdVezesUnit = 0;

    let menorUnit = Number.POSITIVE_INFINITY;
    let maiorUnit = 0;

    const itensCalculados = [];

    if (Array.isArray(movimentoItems) && movimentoItems.length) {
        for (const item of movimentoItems) {
            const qtd = helpers.nz(item.quantidade);
            const unit = helpers.nz(item.valorUnitario);
            const fator = considerarFator ? helpers.nz(item.fator, 1) : 1;

            // Base bruta do item
            const base = helpers.round2(qtd * unit * fator);
            const baseC = helpers.toCents(base);

            // Desconto do item: usa valor se existir; senão, percentual
            const descontoValInformado = helpers.nz(item.descontoValor, helpers.nz(item.desconto)); // compat: descontoValor ou desconto
            const descontoPct = helpers.nz(item.descontoPercentual);
            const descontoItem = descontoValInformado > 0 ? descontoValInformado : helpers.round2((base * descontoPct) / 100);
            const descontoItemC = Math.min(helpers.toCents(descontoItem), baseC); // nunca maior que a base

            // Total sem financeiro
            const totalSemFinCItem = Math.max(0, baseC - descontoItemC);

            // Desconto financeiro por item (rateado previamente, se existir)
            // Aceita 'descontoFinanceiroItem' (preferido) ou 'descontoFinanceiro'
            const descFinItem = helpers.nz(item.descontoFinanceiroItem, helpers.nz(item.descontoFinanceiro));
            const descFinItemC = Math.min(helpers.toCents(descFinItem), totalSemFinCItem);

            // Total com financeiro
            const totalComFinCItem = Math.max(0, totalSemFinCItem - descFinItemC);

            // Acréscimos por item (se existirem). Adicione chaves conforme seu modelo.
            const acrescimosItem = helpers.sumKnownKeys(item, [
                "frete",
                "freteValor",
                "acrescimo",
                "acrescimoValor",
                "outrosAcrescimos",
                "seguro",
                "despesaAcessoria",
            ]);
            const acrescimosItemC = helpers.toCents(acrescimosItem);

            // Acúmulos
            brutoC += baseC;
            descontoItensC += descontoItemC;
            subtotalSemFinC += totalSemFinCItem;
            descontoFinC += descFinItemC;
            subtotalComFinC += totalComFinCItem;
            acrescimosC += acrescimosItemC;

            // Pesos (se Produto presente)
            if (item.Produto) {
                pesoBruto += helpers.nz(qtd * helpers.nz(item.Produto.pesoBruto));
                pesoLiquido += helpers.nz(qtd * helpers.nz(item.Produto.pesoLiquido));
            }

            // Estatísticas de unitário
            if (unit > 0 && qtd > 0) {
                somaQtdParaMedia += qtd;
                somaQtdVezesUnit += qtd * unit;
                if (unit < menorUnit) menorUnit = unit;
                if (unit > maiorUnit) maiorUnit = unit;
            }

            somaQtd += qtd;

            if (retornaItensCalculados) {
                itensCalculados.push({
                    ...item,
                    _base: helpers.fromCents(baseC),
                    _descontoItem: helpers.fromCents(descontoItemC),
                    _totalSemFinanceiro: helpers.fromCents(totalSemFinCItem),
                    _descontoFinanceiroItem: helpers.fromCents(descFinItemC),
                    _totalComFinanceiro: helpers.fromCents(totalComFinCItem),
                    _acrescimosItem: helpers.fromCents(acrescimosItemC),
                });
            }
        }
    }

    const valores = {
        bruto: helpers.fromCents(brutoC),
        descontoItens: helpers.fromCents(descontoItensC),
        subtotalSemFinanceiro: helpers.fromCents(subtotalSemFinC),
        descontoFinanceiro: helpers.fromCents(descontoFinC),
        subtotalComFinanceiro: helpers.fromCents(subtotalComFinC),
        acrescimos: helpers.fromCents(acrescimosC),
        total: helpers.fromCents(subtotalComFinC + acrescimosC),
    };

    const pesos = {
        bruto: helpers.round2(pesoBruto),
        liquido: helpers.round2(pesoLiquido),
    };

    const precoUnitario = {
        maior: helpers.round2(maiorUnit || 0),
        menor: helpers.round2(isFinite(menorUnit) ? menorUnit : 0),
        mediaPonderada: somaQtdParaMedia > 0 ? helpers.round2(somaQtdVezesUnit / somaQtdParaMedia) : 0,
    };

    const quantidades = {
        itens: Array.isArray(movimentoItems) ? movimentoItems.length : 0,
        unidades: helpers.round2(somaQtd),
    };

    const resultado = { valores, pesos, quantidades, precoUnitario };
    if (retornaItensCalculados) resultado.itensCalculados = itensCalculados;
    return resultado;
};

const objetoDeFuncoes = {
    obterValoresByMovimentoItens,
};

export default objetoDeFuncoes;
